import { Button, Form, Input, Select, Space } from "antd";
import { Trans, useTranslation } from "next-i18next";
import { useEffect, useRef, useState, useMemo } from "react";

import { getCookie } from "cookies-next";
import resourceServices from "@/services/resources";
import registerServices from "@/services/registerSolution";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { useRouter } from "next/router";

export function LinkText({ href, children }) {
  return (
    <a
      href={href || ""}
      target="_blank"
      className="text-primary-500 hover:underline"
    >
      {children}
    </a>
  );
}

export default function FormContact({
  solutions,
  onChangeSolution,
  onSubmitSuccess,
}) {
  const { t } = useTranslation("common");
  const [form] = Form.useForm();
  const router = useRouter();
  const isMounted = useRef(true);

  const positions = ["CTO", "CEO", "Manager", "Team Lead", "Employee"];

  const [countries, setCountries] = useState([]);
  const [countryPhoneCode, setCountryPhoneCode] = useState("");
  const [submiting, setSubmiting] = useState(false);
  const [countryCode, setCountryCode] = useState("VN");

  const departments = useMemo(() => {
    return [
      {
        label: t("department.item_1", {ns: "contact-sales"}),
        value: "sales_marketing"
      },
      {
        label: t("department.item_2", {ns: "contact-sales"}),
        value: "cybersecurity_information_security"
      },
      {
        label: t("department.item_3", {ns: "contact-sales"}),
        value: "finance_accounting"
      },
      {
        label: t("department.item_4", {ns: "contact-sales"}),
        value: "engineering_product_development"
      },
      {
        label: t("department.item_5", {ns: "contact-sales"}),
        value: "other"
      }
    ]
  }, [t])

  useEffect(() => {
    isMounted.current = true;
    form.setFieldValue("products", "vulnerability_assessment");
    form.setFieldValue("job_title", "CTO");
    fetchCountry();

    // Cleanup function to prevent state updates after unmount
    return () => {
      isMounted.current = false;
    };
  }, []);

  const fetchCountry = async () => {
    try {
      const [countries, currentLanguage] = await Promise.all([
        resourceServices.fetchCountry(),
        resourceServices.fetchCurrentCountry(),
      ]);

      if (!isMounted.current) return;

      setCountries(countries);
      if (countries.length && currentLanguage) {
        const data = countries.find(
          (c) => c.country_code === currentLanguage.country_code
        );
        if (!!data) {
          setCountryPhoneCode(data.country_phone_code);
        }
      }
    } catch (error) {
      console.error("Error fetching country data:", error);
    }
  };

  const hanldeChangeCountry = (value) => {
    const selectedCountry = countries.find(
      (c) => c.country_phone_code === value
    );
    if (!selectedCountry) return;

    setCountryPhoneCode(selectedCountry.country_phone_code);
    setCountryCode(selectedCountry.country_code);
  };

  const payloadProducts = useMemo(() => {
    const products = form.getFieldValue('products');
    switch (products) {
      case "vulnerability_assessment":
        return ['penetration_testing', 'cloud_security_audit', 'red_teaming, performance_testing']
      case "vulnerability_management":
        return ['endpoint', 'whitehub_bug_bounty', 'vulnerability_management', 'security_monitoring', 'incident_response ']
      case "data_loss_prevention":
        return ['data_leak', 'password_manager', 'secrets_manager', 'endpoint']
      case "trust_center":
        return ['trust_center', 'security_policy_development', 'security_training']
      case "smart_contract_audit":
        return ['smart_contract_audit', 'blockchain_protocol_audit', 'safechain']
      default:
        return []
    }
  }, [form.getFieldValue('products')])

  const { executeRecaptcha } = useGoogleReCaptcha();

  const onFinish = (values) => {
    if (!executeRecaptcha) {
      console.log("Execute recaptcha not yet available");
      return;
    }
    executeRecaptcha("requestForm").then(async (gReCaptchaToken) => {
      try {
        setSubmiting(true);

        // Return early if component unmounted
        if (!isMounted.current) return;

        const payload = {
          captcha_code: gReCaptchaToken,
          email: values.email || "",
          fullname: values.fullname || "",
          organization_name: values.fullname || "",
          phone_number: values.phone_number
            ? `${countryPhoneCode}${values.phone_number}`
            : "",
          job_title: values.job_title || "",
          department: values.department || "",
          country_code: countryCode || "",
          products: payloadProducts || [],
          message: values.message || "",
          source_link: window.location.href,
        };
        const utm_source = getCookie("utm_source");
        const utm_campaign = getCookie("utm_campaign");
        if (utm_source) {
          payload.source_method = "ads";
          payload.utm_source = utm_source;
        } else {
          payload.source_method = "organic";
        }
        if (utm_campaign) {
          payload.utm_campaign = utm_campaign;
        }
        const firstLink = sessionStorage.getItem("first_link");
        if (firstLink) {
          payload.first_link = firstLink;
        }
        await registerServices.register(payload);

        // Return early if component unmounted
        if (!isMounted.current) return;

        window.dataLayer.push({ event: "formsubmit", formId: router.asPath });
        window.scrollTo(0, 0);
        onSubmitSuccess();
        setSubmiting(false);
      } catch (error) {
        if (isMounted.current) {
          setSubmiting(false);
        }
        console.log(error);
      }
    });
  };

  return (
    <Form
      layout={"vertical"}
      form={form}
      name="form-contact-sale"
      onFinish={onFinish}
    >
      <Form.Item
        rules={[
          {
            required: true,
            message: t("validate.input_required", {
              field: t("form.name"),
            }),
          },
        ]}
        className="mb-6"
        name="fullname"
        label={t("form.name")}
      >
        <Input placeholder={t("form.placeholder_name")} />
      </Form.Item>
      <Form.Item
        rules={[
          {
            required: true,
            message: t("validate.input_required", {
              field: t("form.position"),
            }),
          },
        ]}
        className="mb-6"
        name="job_title"
        label={t("form.position")}
      >
        <Select
          allowClear={false}
          placeholder={t("form.position")}
          options={positions.map((item) => ({
            label: item,
            value: item,
          }))}
        />
      </Form.Item>
      <Form.Item
        rules={[
          {
            required: true,
            message: t("validate.input_required", {
              field: t("form.department", {ns: "contact-sales"}),
            }),
          },
        ]}
        className="mb-6"
        name="department"
        label={t("form.department", {ns: "contact-sales"})}
      >
        <Select
          allowClear={false}
          options={departments}
          placeholder={t("form.placeholder_department", {ns: "contact-sales"})}
        />
      </Form.Item>
      <Form.Item
        rules={[
          {
            required: true,
            message: t("validate.input_required", {
              field: t("form.email"),
            }),
          },
          {
            type: "email",
            message: t("validate.invalid", {
              field: t("form.email"),
            }),
          },
        ]}
        className="mb-6"
        name="email"
        label={t("form.email")}
      >
        <Input placeholder={t("form.placeholder_email")} />
      </Form.Item>
      <Form.Item
        label={t("form.phone")}
        rules={[
          {
            required: true,
            message: t("validate.input_required", {
              ns: "common",
              field: t("form.phone"),
            }),
          },
          {
            pattern: new RegExp(/^[0-9]+$/),
            message: t("validate.invalid", {
              ns: "common",
              field: t("form.phone"),
            }),
          },
        ]}
        className="mb-6"
        name="phone_number"
      >
        <Space.Compact className="w-full">
          <Select
            value={countryPhoneCode}
            showSearch
            onChange={hanldeChangeCountry}
            allowClear={false}
            placeholder={t("form.placeholder_phone")}
            className="!rounded-tr-none w-[100px]"
            options={countries.map((item) => ({
              label: item.country_phone_code,
              value: item.country_phone_code,
            }))}
          />
          <Input
            name="phone"
            className="w-full"
            placeholder={t("form.phone")}
          />
        </Space.Compact>
      </Form.Item>
      <Form.Item
        rules={[
          {
            required: true,
            message: t("validate.input_required", {
              field: t("form.solution_interested"),
            }),
          },
        ]}
        className="mb-6"
        name="products"
        label={t("form.solution_interested")}
      >
        <Select
          onChange={onChangeSolution}
          allowClear={false}
          options={solutions.map((item) => ({
            label: item.label,
            value: item.value,
          }))}
        />
      </Form.Item>

      <Form.Item
        className="mb-6"
        name="message"
        label={t("form.message_description")}
      >
        <Input.TextArea
          rows={3}
          placeholder={t("form.placeholder_message_description")}
          id="form-request-banner-message"
        />
      </Form.Item>
      <p className="mb-6 font-medium text-md">
        <Trans
          i18nKey="form.click_agree_policy"
          t={t}
          components={{
            TermsOfUse: (
              <LinkText
                href={`${router.locale === "vi" ? "/vi" : ""}/terms-of-service`}
              />
            ),
            Privacy: (
              <LinkText
                href={`${router.locale === "vi" ? "/vi" : ""}/privacy`}
              />
            ),
          }}
        />
      </p>
      <Form.Item className="mb-0">
        <Button htmlType="submit" type="primary" loading={submiting} block>
          {t("submit", { ns: "contact-sales" })}
        </Button>
      </Form.Item>
    </Form>
  );
}
